import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Spinner,
  Alert,
  Text,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@razorpay/blade/components';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import api from 'api';
import { aiWorkflowQueryKeys } from 'api/aiWorkflows';
import { UpdateWorkflowRequest } from 'api/types/aiWorkflowTypes';
import { showToastViaEvent } from 'utils/ToastEvents';
import { AppError } from 'utils/AppError';
import { AIWorkflowConfig } from '../../types';
import { EmptyState } from '../EmptyState';
import { WorkflowList } from '../WorkflowList';
import { convertApiWorkflowToListItem } from '../../utils';

const MyFlowsView = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<AIWorkflowConfig | null>(null);

  const {
    data: workflowsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [aiWorkflowQueryKeys.workflows],
    queryFn: () => api.aiWorkflows.getWorkflows(),
    onError: (err: AppError) => {
      showToastViaEvent({
        text: err.message || 'Failed to load workflows',
        type: 'danger',
        timeout: 3000,
      });
    },
  });

  const deleteWorkflowMutation = useMutation({
    mutationFn: (workflowId: number) => api.aiWorkflows.deleteWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries([aiWorkflowQueryKeys.workflows]);
      showToastViaEvent({
        text: 'Workflow deleted successfully',
        type: 'success',
        timeout: 3000,
      });
      setShowDeleteModal(false);
      setWorkflowToDelete(null);
    },
    onError: (err: AppError) => {
      showToastViaEvent({
        text: err.message || 'Failed to delete workflow',
        type: 'danger',
        timeout: 3000,
      });
    },
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: ({ workflowId, isEnabled }: { workflowId: number; isEnabled: boolean }) =>
      api.aiWorkflows.updateWorkflow({
        workflowId,
        requestData: { is_active: isEnabled } as UpdateWorkflowRequest,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([aiWorkflowQueryKeys.workflows]);
      showToastViaEvent({
        text: `Workflow ${variables.isEnabled ? 'enabled' : 'disabled'} successfully`,
        type: 'success',
        timeout: 3000,
      });
    },
    onError: (err: AppError) => {
      showToastViaEvent({
        text: err.message || 'Failed to update workflow',
        type: 'danger',
        timeout: 3000,
      });
    },
  });

  const workflows = (workflowsData?.workflows || []).map(convertApiWorkflowToListItem);

  const handleCreateClick = () => {
    history.push(routePaths.aiWorkflow.create);
  };

  const handleEdit = (workflow: AIWorkflowConfig) => {
    history.push(`${routePaths.aiWorkflow.edit}?workflowId=${workflow.id}`);
  };

  const handleDelete = (workflow: AIWorkflowConfig) => {
    setWorkflowToDelete(workflow);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!workflowToDelete?.id) return;
    deleteWorkflowMutation.mutate(workflowToDelete.id);
  };

  const handleToggle = (workflow: AIWorkflowConfig, isEnabled: boolean) => {
    if (!workflow.id) return;
    updateWorkflowMutation.mutate({ workflowId: workflow.id, isEnabled });
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
        <Spinner size="large" accessibilityLabel="Loading workflows..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="spacing.6">
        <Alert
          color="negative"
          description="Failed to load workflows. Please try again."
          isDismissible
          onDismiss={() => {}}
          actions={{
            primary: {
              text: 'Retry',
              onClick: () => refetch(),
            },
          }}
        />
      </Box>
    );
  }

  return (
    <>
      {workflows.length === 0 ? (
        <EmptyState
          title="No flows created"
          description="Ready to create your first flow?"
          buttonText="See templates"
          onButtonClick={handleCreateClick}
        />
      ) : (
        <WorkflowList
          workflows={workflows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggle={handleToggle}
        />
      )}

      <Modal isOpen={showDeleteModal} onDismiss={() => setShowDeleteModal(false)}>
        <ModalHeader title="Delete Workflow" />
        <ModalBody>
          <Text>
            Are you sure you want to delete "{workflowToDelete?.name}"? This action cannot be
            undone.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.4" justifyContent="flex-end">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              isDisabled={deleteWorkflowMutation.isLoading}>
              Cancel
            </Button>
            <Button
              color="negative"
              onClick={handleConfirmDelete}
              isLoading={deleteWorkflowMutation.isLoading}>
              Delete
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { MyFlowsView };
